import {ArrowTopRightOnSquareIcon} from "@heroicons/react/20/solid";
import {ReactNode, useState} from "react";

export type OtsGalleryImage = {
  id: string;
  src: string;
  /** What the picture shows; falls back to the caption, then a numbered label. */
  alt?: string;
  caption?: ReactNode;
  /** Draws a file tile, this icon over the name, instead of a thumbnail: for documents that are not pictures. */
  icon?: ReactNode;
  /** The file name under a file tile's icon. */
  name?: string;
};

export type OtsImageGalleryProps = {
  images: OtsGalleryImage[];
  ariaLabel: string;
  /** Thumbnail edge in pixels. */
  thumbnailSize?: number;
  /** Shown instead of the gallery when there are no images. */
  emptyState?: ReactNode;
  className?: string;
  /** Opens a tile somewhere else (a viewer dialog) instead of showing it large in place. */
  onOpen?: (image: OtsGalleryImage, index: number) => void;
};

/** Thumbnails that open in place: pick one to see it large, with its caption and a link to the original. */
export const OtsImageGallery = ({images, ariaLabel, thumbnailSize = 64, emptyState = null, className, onOpen}: OtsImageGalleryProps) => {
  const [openId, setOpenId] = useState<string>();
  const open = images.find(image => image.id === openId);
  if (!images.length) return <>{emptyState}</>;

  const altFor = (image: OtsGalleryImage, index: number) =>
    image.alt ?? image.name ?? (typeof image.caption === "string" ? image.caption : `Picture ${index + 1}`);

  const tileClassName = "flex items-center justify-center overflow-hidden rounded-md border bg-[var(--ots-surface-variant)] outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]";
  const tileContent = (image: OtsGalleryImage) => image.icon
    ? <span className="flex w-full flex-col items-center gap-0.5 px-1 text-[var(--ots-text-muted)]">
      <span aria-hidden="true" className="size-6 [&>svg]:size-full">{image.icon}</span>
      {image.name && <span className="w-full truncate text-center text-[10px] leading-3">{image.name}</span>}
    </span>
    : <img src={image.src} alt="" loading="lazy" className="size-full object-cover"/>;

  return <div className={["space-y-2", className].filter(Boolean).join(" ")}>
    <ul aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {images.map((image, index) => <li key={image.id}>
        {!onOpen && image.icon ? <a
          href={image.src}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${altFor(image, index)}`}
          style={{width: thumbnailSize, height: thumbnailSize}}
          className={[tileClassName, "border-[var(--ots-outline-variant)] hover:border-[var(--ots-primary)]"].join(" ")}>
          {tileContent(image)}
        </a> : <button
          type="button"
          aria-label={`${onOpen ? "Open" : "View"} ${altFor(image, index)}`}
          aria-pressed={onOpen ? undefined : image.id === openId}
          onClick={() => onOpen ? onOpen(image, index) : setOpenId(image.id === openId ? undefined : image.id)}
          style={{width: thumbnailSize, height: thumbnailSize}}
          className={[
            tileClassName,
            image.id === openId ? "border-[var(--ots-primary)] ring-2 ring-[var(--ots-primary)]" : "border-[var(--ots-outline-variant)] hover:border-[var(--ots-primary)]",
          ].join(" ")}>
          {tileContent(image)}
        </button>}
      </li>)}
    </ul>
    {open && <figure className="overflow-hidden rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)]">
      <img src={open.src} alt={altFor(open, images.indexOf(open))} className="max-h-80 w-full object-contain"/>
      <figcaption className="!flex items-center justify-between gap-2 bg-[var(--ots-surface)] px-2 py-1.5 text-xs text-[var(--ots-text-muted)]">
        <span className="min-w-0 truncate">{open.caption}</span>
        <a
          href={open.src}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 font-semibold text-[var(--ots-primary)] hover:underline">
          Open original<ArrowTopRightOnSquareIcon aria-hidden="true" className="size-3.5"/>
        </a>
      </figcaption>
    </figure>}
  </div>;
};
